<script lang="ts">
	import { createEventDispatcher, onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	//Design system components
	import { Alert, EngagingLoader, Button } from '@Shelly/shelly-design-system';
	//Helpers
	import WebBluetoothClient from 'src/helpers/web_bluetooth/web_bluetooth_client';
	import WebBluetoothEvents from 'src/helpers/web_bluetooth/web_bluetooth_events';
	import { wait } from 'src/helpers/utilities';
	import { buildBleDisableConfig } from 'src/helpers/ble_devices_helpers';
	import { checkDeviceAccount } from 'src/helpers/web_bluetooth/web_bluetooth_inclusion_helpers';
	import BleWebInclusionDeviceExist from './BleWebInclusionDeviceExist.svelte';
	import TransitionWrapper from '../Utility/TransitionWrapper.svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import { isTestUser } from 'src/helpers/user_helpers';
	//Types
	import type { Device } from 'src/types/Device';
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
	import type { WebBLEEvent } from 'src/helpers/web_bluetooth/web_bluetooth_types';
	import type { DeviceInfoG2 } from 'src/types/DeviceInfoG2';
	import BleWebInclusionWiFiStatus from './BleWebInclusionWiFiStatus.svelte';
	import BleWebInclusionWiFiForm from './BleWebInclusionWiFiForm.svelte';

	export let deviceAlreadyExist: Device = null;
	export let client: WebBluetoothClient;
	export let waitForResponse = false;
	export let rpcError = '';

	let status: DeviceStatusG2;
	let settings: DeviceSettingsG2;
	let deviceInfo: DeviceInfoG2;
	let wifiForm: BleWebInclusionWiFiForm;
	let disableAP = false;
	let APDisabled = false;
	let disableBluetooth = false;
	let progressStatus = 'Loading...';
	let lastFailedMethod = '';
	let progress = 0;
	let connectionStatus: 'success' | 'failed' | 'pending' = 'pending';
	let initGetStatus = false;

	onMount(() => {
		client.addEventListener(WebBluetoothEvents.DATA, onRPCData);
		progressStatus = $format('getting_device_information', {
			default: 'Getting device information',
		});
		callRPC('Shelly.GetStatus');
		return () => {
			client.removeEventListener(WebBluetoothEvents.DATA, onRPCData);
		};
	});

	const dispatch = createEventDispatcher();
	const callRPC = (method: string, params: any = null) => {
		waitForResponse = true;
		client.call(method, params);
	};
	const onRPCData = async (e: WebBLEEvent) => {
		waitForResponse = false;
		const data = e.detail.data;
		rpcError = '';
		if (import.meta.env.DEV) {
			console.log('onRPCData', data);
		}
		if (data.method == 'Wifi.Scan') {
			return;
		} else if (data.result?.model == 'BluTRV') {
			rpcError = $format('error_blu_trv_failed_inclusion', {
				default:
					'The device is a BluTRV and cannot be included. Please use the Shelly BLU Gateway Gen3 for BluTRV pairing.',
			});
			return;
		} else if (data.error) {
			if (data.error?.code == 400) {
				rpcError = $format('failed_device_authentication_error_message', {
					default:
						"The device cannot be included in the Shelly app because it has authentication enabled. Please use the device's local web interface to unlock it and then try again.",
				});
			} else {
				rpcError =
					data.error?.message ||
					$format('error_global', {
						default: 'Something went wrong!',
					});
			}
			lastFailedMethod = data.method;
			return;
		}
		switch (data.method) {
			case 'Shelly.GetStatus':
				status = data.result;
				if (!initGetStatus) {
					initGetStatus = true;
					progressStatus = $format('getting_settings', {
						default: 'Getting settings',
					});
					progress = 33;
					callRPC('Shelly.GetConfig');
				} else {
					if (status?.wifi?.status == 'connected') {
						//The device is connected but doesn't have an IP
						callRPC('Shelly.GetStatus');
					} else {
						const connectionSuccess = status?.wifi?.status == 'got ip';
						connectionStatus = connectionSuccess ? 'success' : 'failed';
					}
				}
				break;
			case 'Shelly.GetConfig':
				settings = data.result;
				progressStatus = $format('getting_status', {
					default: 'Getting status',
				});
				progress = 66;
				callRPC('Shelly.GetDeviceInfo');
				break;
			case 'Shelly.GetDeviceInfo':
				progressStatus = $format('device_processed', {
					default: 'Finishing up...',
				});
				progress = 100;
				await wait(500);
				deviceInfo = data.result;
				deviceAlreadyExist = checkDeviceAccount(deviceInfo);
				break;
			case 'Wifi.SetConfig':
				if (connectionStatus == 'success') {
					APDisabled = true;
					finalStepSetting();
				} else {
					waitForResponse = true;
					await wait(5000);
					callRPC('Shelly.GetStatus');
				}
				break;
			case 'Ble.SetConfig':
				completeSetup();
				break;
			case 'Cloud.SetConfig':
				dispatch('finish', { deviceInfo, status, settings });
				break;
		}
	};

	export const handleNext = () => {
		if (connectionStatus == 'success') {
			finalStepSetting();
		} else if (connectionStatus == 'failed') {
			// Connection is not successful but proceed with inclusion
			completeSetup();
		} else {
			validateWifiSetting();
		}
	};

	const finalStepSetting = () => {
		if (!APDisabled && disableAP && settings.wifi?.ap?.enable === true) {
			const config = { ap: { enable: false } };
			callRPC('Wifi.SetConfig', { config });
		} else if (disableBluetooth) {
			callRPC('Ble.SetConfig', {
				config: buildBleDisableConfig(),
			});
		} else {
			completeSetup();
		}
	};

	const completeSetup = () => {
		callRPC('Cloud.SetConfig', {
			config: { enable: true },
		});
	};

	const validateWifiSetting = () => {
		if (rpcError) {
			callRPC(lastFailedMethod);
			return;
		}
		const validate = wifiForm.validateWifiSetting();
		if (validate) {
			if (validate?.['ssid']) {
				const config = { sta: validate };
				callRPC('Wifi.SetConfig', { config });
			} else {
				completeSetup();
			}
		}
	};
</script>

{#if rpcError}
	<TransitionWrapper outDuration={0}>
		<Alert type="danger" heading={rpcError} /></TransitionWrapper
	>
{:else if deviceInfo}
	{#if deviceAlreadyExist}
		<TransitionWrapper outDuration={0}>
			<BleWebInclusionDeviceExist device={deviceAlreadyExist} /></TransitionWrapper
		>
	{:else if connectionStatus == 'success'}
		<TransitionWrapper outDuration={0}>
			<BleWebInclusionWiFiStatus {deviceInfo} {status} bind:disableAP bind:disableBluetooth />
		</TransitionWrapper>
	{:else}
		{#if connectionStatus == 'failed'}
			<Alert
				type="warning"
				class="mb-m"
				heading={$format('alert_heading_ble_inclusion_connection_failed_title', {
					default: 'Connection to the device via WiFi is not successful!',
				})}
				message={$format('inclusion_device_is_already_present_text', {
					default: 'Do you want to proceed with inclusion?',
				})}
			/>
			<Button
				size="m"
				class="width-full mb-m"
				responsiveSize={{
					sm: 'm',
				}}
				disabled={waitForResponse}
				variant="secondary"
				on:click={() => callRPC('Shelly.GetStatus')}
			>
				{$format('get_status', { default: 'Get Status' })}
			</Button>
		{/if}
		<TransitionWrapper outDuration={0}>
			<BleWebInclusionWiFiForm
				bind:this={wifiForm}
				on:change={() => (connectionStatus = 'pending')}
				{client}
				{status}
				{settings}
			/>
		</TransitionWrapper>
	{/if}
	{#if isTestUser()}
		<Alert
			class="mb-s mt-s"
			type="warning"
			heading={'This functionality is under development and used for testing.'}
			isHeadingBold
			message={'You can see this because you are using a test user account.'}
		/>
		<Button
			size="m"
			class="width-full mb-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="secondary"
			on:click={() => callRPC('Shelly.Reboot')}
			>{capFirstChar(
				$format('button_reboot', {
					default: 'Reboot device',
				}),
			)}</Button
		>
		<Button
			size="m"
			class="width-full mb-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="secondary"
			on:click={() => callRPC('Shelly.FactoryReset')}
			>{capFirstChar(
				$format('button_factory_reset', {
					default: 'Factory reset',
				}),
			)}</Button
		>
	{/if}
{:else}
	<TransitionWrapper outDuration={0}>
		<div class="d-flex justify-content-center">
			<EngagingLoader
				show
				title={progressStatus}
				{progress}
				imagesSrc={[
					'/images/engagingLoader/blu-button1-device.svg',
					'/images/engagingLoader/ht-gen3.svg',
					'/images/engagingLoader/blu-motion-device.svg',
					'/images/engagingLoader/1pm-mini-gen3.svg',
					'/images/engagingLoader/shelly-plus-1.svg',
				]}
				successImageSrc=""
				errorImageSrc=""
			/>
		</div>
	</TransitionWrapper>
{/if}
